import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, Building2, CreditCard, Send, Clock, CheckCircle, XCircle, Loader2, Wallet, ArrowDownToLine, History } from 'lucide-react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

export default function ProviderWithdrawalsPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { language } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [isBankDialogOpen, setIsBankDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankForm, setBankForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    clabe: '',
    account_type: 'checking',
    country: 'MX'
  });

  const fetchData = useCallback(async () => {
    try {
      const [balanceRes, bankRes, withdrawalsRes] = await Promise.all([
        api().get('/provider/balance'),
        api().get('/provider/bank-account'),
        api().get('/provider/withdrawals')
      ]);
      setBalance(balanceRes.data);
      setBankAccount(bankRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    if (!bankForm.account_holder_name || !bankForm.bank_name || !bankForm.account_number) {
      toast.error(language === 'en' ? 'Please fill all required fields' : 'Por favor llena todos los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      await api().post('/provider/bank-account', bankForm);
      toast.success(language === 'en' ? 'Bank account saved!' : '¡Cuenta bancaria guardada!');
      setIsBankDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestWithdrawal = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 10) {
      toast.error(language === 'en' ? 'Minimum withdrawal is $10 USD' : 'El retiro mínimo es $10 USD');
      return;
    }
    if (amount > balance?.available_balance) {
      toast.error(language === 'en' ? 'Insufficient balance' : 'Saldo insuficiente');
      return;
    }

    setSubmitting(true);
    try {
      await api().post('/provider/withdrawals', { amount });
      toast.success(language === 'en' ? 'Withdrawal completed!' : '¡Retiro completado!');
      setIsWithdrawDialogOpen(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-gold text-foreground', icon: Clock, label: language === 'en' ? 'Pending' : 'Pendiente' },
      processing: { color: 'bg-caribbean text-white', icon: Loader2, label: language === 'en' ? 'Processing' : 'Procesando' },
      completed: { color: 'bg-green-500 text-white', icon: CheckCircle, label: language === 'en' ? 'Completed' : 'Completado' },
      rejected: { color: 'bg-destructive text-white', icon: XCircle, label: language === 'en' ? 'Rejected' : 'Rechazado' }
    };
    const { color, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon size={14} className={status === 'processing' ? 'animate-spin' : ''} />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Withdrawals' : 'Retiros'}
        right={
          <Link to="/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </Link>
        }
      />
      <main className="px-4 py-4 space-y-4" data-testid="provider-withdrawals-page">
        {/* Balance Card */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-caribbean to-caribbean/80 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet size={24} />
                <span className="text-white/80">
                  {language === 'en' ? 'Available Balance' : 'Saldo Disponible'}
                </span>
              </div>
            </div>
            <p className="font-heading text-4xl font-bold mb-2">
              ${balance?.available_balance?.toFixed(2) || '0.00'} <span className="text-lg font-normal">USD</span>
            </p>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-white/20 text-sm">
              <div>
                <p className="text-white/70">{language === 'en' ? 'Total Earnings' : 'Ganancias Totales'}</p>
                <p className="font-bold">${balance?.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <p className="text-white/70">{language === 'en' ? 'Total Withdrawn' : 'Total Retirado'}</p>
                <p className="font-bold">${balance?.total_withdrawn?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            {balance?.pending_release > 0 && (
              <div className="mt-4 p-3 bg-gold/20 rounded-xl">
                <p className="text-sm text-gold">
                  <Clock size={14} className="inline mr-1" />
                  {language === 'en' 
                    ? `$${balance.pending_release.toFixed(2)} pending release (waiting for experience to complete)`
                    : `$${balance.pending_release.toFixed(2)} pendiente de liberación (esperando que termine la experiencia)`}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            data-testid="request-withdrawal-btn"
            onClick={() => {
              if (!bankAccount) {
                toast.error(language === 'en' ? 'Please add a bank account first' : 'Por favor agrega una cuenta bancaria primero');
                setIsBankDialogOpen(true);
                return;
              }
              setIsWithdrawDialogOpen(true);
            }}
            disabled={!balance?.available_balance || balance.available_balance < 10}
            className="h-16 rounded-2xl bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
          >
            <ArrowDownToLine size={20} className="mr-2" />
            {language === 'en' ? 'Request Withdrawal' : 'Solicitar Retiro'}
          </Button>
          <Button
            data-testid="manage-bank-btn"
            onClick={() => setIsBankDialogOpen(true)}
            variant="outline"
            className="h-16 rounded-2xl font-semibold"
          >
            <Building2 size={20} className="mr-2" />
            {language === 'en' ? 'Bank Account' : 'Cuenta Bancaria'}
          </Button>
        </div>

        {/* Bank Account Info */}
        {bankAccount && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard size={18} />
                {language === 'en' ? 'Linked Bank Account' : 'Cuenta Bancaria Vinculada'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{bankAccount.bank_name}</p>
                  <p className="text-sm text-muted-foreground">
                    ****{bankAccount.account_number_last4} • {bankAccount.account_type === 'checking' ? (language === 'en' ? 'Checking' : 'Cheques') : (language === 'en' ? 'Savings' : 'Ahorro')}
                  </p>
                </div>
                {bankAccount.is_verified ? (
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle size={14} className="mr-1" />
                    {language === 'en' ? 'Verified' : 'Verificada'}
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    {language === 'en' ? 'Pending Verification' : 'Pendiente Verificación'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Withdrawal History */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History size={20} />
              {language === 'en' ? 'Withdrawal History' : 'Historial de Retiros'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {withdrawals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Wallet size={48} className="mx-auto mb-4 opacity-50" />
                <p>{language === 'en' ? 'No withdrawals yet' : 'Sin retiros aún'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {withdrawals.map((w) => (
                  <div
                    key={w.id}
                    data-testid={`withdrawal-${w.id}`}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                  >
                    <div>
                      <p className="font-heading font-bold text-lg">${w.amount.toFixed(2)} USD</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(w.created_at).toLocaleDateString()} • {w.bank_name} ****{w.account_last4}
                      </p>
                      {w.admin_notes && (
                        <p className="text-xs text-muted-foreground mt-1 italic">"{w.admin_notes}"</p>
                      )}
                    </div>
                    {getStatusBadge(w.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Request Withdrawal Dialog */}
      <Dialog open={isWithdrawDialogOpen} onOpenChange={setIsWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'en' ? 'Instant Withdrawal' : 'Retiro Instantáneo'}</DialogTitle>
            <DialogDescription>
              {language === 'en' 
                ? `Available balance: $${balance?.available_balance?.toFixed(2) || '0.00'} USD. Withdrawals are processed instantly.`
                : `Saldo disponible: $${balance?.available_balance?.toFixed(2) || '0.00'} USD. Los retiros se procesan al instante.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'en' ? 'Amount to Withdraw' : 'Monto a Retirar'}</Label>
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-muted-foreground" />
                <Input
                  type="number"
                  data-testid="withdraw-amount-input"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  min="10"
                  max={balance?.available_balance}
                  className="h-12 text-lg"
                />
                <span className="text-muted-foreground">USD</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'en' ? 'Minimum: $10 USD' : 'Mínimo: $10 USD'}
              </p>
            </div>

            {bankAccount && (
              <div className="p-4 bg-muted rounded-xl">
                <p className="text-sm font-medium mb-1">
                  {language === 'en' ? 'Destination Account' : 'Cuenta Destino'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {bankAccount.bank_name} ****{bankAccount.account_number_last4}
                </p>
              </div>
            )}

            <Button
              data-testid="confirm-withdrawal-btn"
              onClick={handleRequestWithdrawal}
              disabled={submitting || !withdrawAmount || parseFloat(withdrawAmount) < 10}
              className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
            >
              {submitting ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <Send size={18} className="mr-2" />
              )}
              {submitting 
                ? (language === 'en' ? 'Processing...' : 'Procesando...') 
                : (language === 'en' ? 'Withdraw Now' : 'Retirar Ahora')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Account Dialog */}
      <Dialog open={isBankDialogOpen} onOpenChange={setIsBankDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{language === 'en' ? 'Bank Account' : 'Cuenta Bancaria'}</DialogTitle>
            <DialogDescription>
              {language === 'en' 
                ? 'Add your bank account details to receive withdrawals'
                : 'Agrega los datos de tu cuenta bancaria para recibir retiros'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveBankAccount} className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'en' ? 'Account Holder Name *' : 'Nombre del Titular *'}</Label>
              <Input
                data-testid="bank-holder-name"
                value={bankForm.account_holder_name}
                onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                placeholder={language === 'en' ? 'Full name as on account' : 'Nombre completo como aparece en la cuenta'}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'en' ? 'Bank Name *' : 'Nombre del Banco *'}</Label>
              <Input
                data-testid="bank-name"
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                placeholder="Ej: BBVA, Santander, Banorte..."
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'en' ? 'Account Number *' : 'Número de Cuenta *'}</Label>
              <Input
                data-testid="bank-account-number"
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                placeholder="1234567890"
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>CLABE ({language === 'en' ? 'Mexico only' : 'Solo México'})</Label>
              <Input
                data-testid="bank-clabe"
                value={bankForm.clabe}
                onChange={(e) => setBankForm({ ...bankForm, clabe: e.target.value })}
                placeholder="18 digits"
                maxLength={18}
                className="h-12"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'en' ? 'Account Type' : 'Tipo de Cuenta'}</Label>
                <Select
                  value={bankForm.account_type}
                  onValueChange={(value) => setBankForm({ ...bankForm, account_type: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">{language === 'en' ? 'Checking' : 'Cheques'}</SelectItem>
                    <SelectItem value="savings">{language === 'en' ? 'Savings' : 'Ahorro'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'en' ? 'Country' : 'País'}</Label>
                <Select
                  value={bankForm.country}
                  onValueChange={(value) => setBankForm({ ...bankForm, country: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MX">México</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="save-bank-btn"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
            >
              {submitting ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <Building2 size={18} className="mr-2" />
              )}
              {submitting 
                ? (language === 'en' ? 'Saving...' : 'Guardando...') 
                : (language === 'en' ? 'Save Bank Account' : 'Guardar Cuenta')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
}
